import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  Chip,
  alpha,
} from '@mui/material';
import {
  Lock,
  TrendingUp,
  Mail,
  AccountBox,
  Search,
} from '@mui/icons-material';

const LimitReachedDialog = ({ open, onClose, limitType, limitData }) => {
  const getLimitIcon = () => {
    switch (limitType) {
      case 'search':
        return <Search sx={{ fontSize: 48, color: '#3b82f6' }} />;
      case 'email':
        return <Mail sx={{ fontSize: 48, color: '#10b981' }} />;
      case 'smtp_accounts':
        return <AccountBox sx={{ fontSize: 48, color: '#8b5cf6' }} />;
      default:
        return <Lock sx={{ fontSize: 48, color: '#f59e0b' }} />;
    }
  };

  const getLimitTitle = () => {
    switch (limitType) {
      case 'search':
        return 'Daily Search Limit Reached';
      case 'email':
        return 'Daily Email Limit Reached';
      case 'smtp_accounts':
        return 'SMTP Account Limit Reached';
      default:
        return 'Limit Reached';
    }
  };

  const getLimitMessage = () => {
    const defaultMessage =
      'You have reached your daily limit. Please purchase a subscription for unlimited access.';
    return limitData?.message || defaultMessage;
  };

  const getLimitDetails = () => {
    if (!limitData) return null;

    if (limitType === 'search' || limitType === 'email') {
      return {
        label: 'Daily Limit',
        value: limitData.daily_limit || limitData.limit,
        used: limitData.used,
      };
    }

    if (limitType === 'smtp_accounts') {
      return {
        label: 'Account Limit',
        value: limitData.limit,
        requested: limitData.requested,
      };
    }

    return null;
  };

  const details = getLimitDetails();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        },
      }}
    >
      <DialogTitle>
        <Stack spacing={2} alignItems="center" sx={{ pt: 2 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: (theme) =>
                `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.1
                )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              border: (theme) =>
                `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            {getLimitIcon()}
          </Box>
          <Typography
            variant="h5"
            fontWeight={700}
            textAlign="center"
            sx={{
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {getLimitTitle()}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <Typography variant="body1">{getLimitMessage()}</Typography>
          </Alert>

          {details && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.5)
                    : alpha(theme.palette.primary.main, 0.05),
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    {details.label}
                  </Typography>
                  <Chip
                    label={details.value}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Stack>

                {details.used !== undefined && (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Used Today
                    </Typography>
                    <Chip
                      label={details.used}
                      size="small"
                      color="warning"
                      variant="filled"
                    />
                  </Stack>
                )}

                {details.requested !== undefined && (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Requested
                    </Typography>
                    <Chip
                      label={details.requested}
                      size="small"
                      color="error"
                      variant="filled"
                    />
                  </Stack>
                )}
              </Stack>
            </Box>
          )}

          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              background: (theme) =>
                `linear-gradient(135deg, ${alpha(
                  theme.palette.success.main,
                  0.1
                )} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
              border: (theme) =>
                `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TrendingUp color="success" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Upgrade to Premium
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Get unlimited searches, emails, and SMTP accounts with our
                premium subscription. Contact admin for activation.
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          sx={{ minWidth: 120 }}
        >
          Close
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{ minWidth: 120 }}
        >
          I Understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LimitReachedDialog;
